import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Box, Tooltip, useTheme, TextField, Button, InputAdornment } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { getEntityDateColumn } from "../../utils/entityDateColumn";

const EMSX_ROUTER_KEY = '7b16a3667dcc8252a857d4a322d4e143';

const BrokerTable = ({ brokers = [], onEdit, onDelete, onAdd }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 1000,
  });

  const filteredRows = useMemo(() => {
    if (!search) return brokers;
    const s = search.toLowerCase();
    return brokers.filter(row =>
      (row.name || "").toLowerCase().includes(s) ||
      (row.abbreviation || "").toLowerCase().includes(s) ||
      String(row.commission || 0).includes(s) ||
      (row.routers && row.routers[EMSX_ROUTER_KEY]?.name?.toLowerCase().includes(s))
    );
  }, [brokers, search]);

  const columns = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 120 },
    { field: "abbreviation", headerName: "Abbreviation", flex: 1, minWidth: 100 },
    {
      field: "commission",
      headerName: "Default Commission",
      flex: 1,
      minWidth: 130,
      valueGetter: (value, row) => row.commission || 0,
    },
    {
      field: "routerEMSX",
      headerName: "Router (EMSX)",
      flex: 1,
      minWidth: 140,
      valueGetter: (value, row) => row.routers?.[EMSX_ROUTER_KEY]?.name || "N/A",
    },
    getEntityDateColumn({
      field: "date",
      headerName: "Created",
      flex: 1,
      minWidth: 120,
    }),
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton color="primary" size="small" onClick={() => params.row.onEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton color="error" size="small" onClick={() => params.row.onDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      width: 100,
      align: 'center',
      headerAlign: 'center',
    },
  ];
  // Attach onEdit/onDelete to each row for easy access in renderCell
  const rows = (filteredRows || []).map((row) => ({ ...row, onEdit, onDelete, id: row.brokerId }));

  const tableBg =
    theme.palette.mode === "dark"
      ? theme.palette.grey[100]
      : theme.palette.background.paper;

  return (
    <Box sx={{  width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search brokers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Add Broker
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) =>
          setPaginationModel({
            page: model.page,
            pageSize: Math.min(model.pageSize, 1000),
          })
        }
        pageSizeOptions={[100, 200, 1000]}
        pagination
        density="compact"
        disableRowSelectionOnClick
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          backgroundColor: tableBg,
          "& .MuiDataGrid-main": {
            backgroundColor: "inherit",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.grey[100],
            borderTop: `1px solid ${theme.palette.divider}`,
          },
        }}
      />
    </Box>
  );
};

export default BrokerTable;
